import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { Tag } from "primereact/tag";
import { ComplaintActionModel } from "../../models/complaints/complaint-model";
import { Toast } from "primereact/toast";

interface DataTableProps {
  data: ComplaintActionModel[];
  handlepagechange: (event: any) => void;
  pageNo: number;
  pageSize: number;
  totalRecordsCount: number;
  complaintType: 'product' | 'service';
}

const ComplaintActionDataTable: React.FC<DataTableProps> = ({
  data = [],
  handlepagechange = () => {},
  pageNo = 1,
  pageSize = 10,
  totalRecordsCount = 0,
  complaintType = 'product',
}) => {
  const { t } = useTranslation();
  const toast = useRef<Toast>(null);

  const formatDate = (dateString?: string) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const getSeverityTag = (severity?: string) => {
    const map: Record<string, "info" | "warning" | "danger" | "success"> = {
      low: "info",
      medium: "warning",
      high: "danger",
      critical: "danger",
    };
    const severityKey = severity?.toLowerCase() || '';
    return <Tag value={severity || 'unknown'} severity={map[severityKey] || "info"} />;
  };

  const getStatusTag = (status?: string) => {
    const map: Record<string, "info" | "warning" | "danger" | "success"> = {
      new: "warning",
      inprogress: "info",
      resolved: "success",
      closed: "success",
      rejected: "danger",
    };
    const statusKey = status?.toLowerCase() || '';
    return <Tag value={status || 'unknown'} severity={map[statusKey] || "info"} />;
  };

  return (
    <div className="col-12">
      <Toast ref={toast} />
      <div className="card border-noround p-2">
        <DataTable
          value={data}
          paginator
          rowsPerPageOptions={[5, 10, 25, 50, 100]}
          first={(pageNo - 1) * pageSize}
          totalRecords={totalRecordsCount}
          rows={pageSize}
          stripedRows
          onPage={handlepagechange}
          paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
          currentPageReportTemplate="{first} to {last} of {totalRecords}"
          emptyMessage="No complaint actions found"
        >
          {complaintType === 'product' ? (
            <Column field="productComplaintId" header="Product Complaint ID"  sortable/>
          ) : (
            <Column field="serviceComplaintId" header="Service Complaint ID" sortable/>
          )}
          <Column field="actionDate" header="Action Date" body={(row) => formatDate(row.actionDate)} sortable/>
          <Column field="description" header="Description" style={{ maxWidth: "200px", wordWrap: "break-word" }} />
          <Column field="correctiveAction" header="Corrective Action" style={{ maxWidth: "200px", wordWrap: "break-word" }}/>
          <Column field="preventiveAction" header="Preventive Action" style={{ maxWidth: "200px", wordWrap: "break-word" }}/>
          <Column 
            field="severity" 
            header="Severity" 
            body={(row) => getSeverityTag(row.severity)} sortable
          />
          <Column 
            field="statusName" 
            header="Status" 
            body={(row) => getStatusTag(row.statusName)} sortable
          />
          <Column field="actionByName" header="Action By" sortable/>
          <Column field="copqInRs" header="COPQ (₹)" body={(row) => row.copqInRs?.toFixed(2) || "0.00"} sortable/>
        </DataTable>
      </div>
    </div>
  );
};

export default ComplaintActionDataTable;